// src/services/notification.service.js
import { api } from './api';

class NotificationService {
    /**
     * Get notifications for current user
     */
    // Normalize snake_case DB rows to camelCase (matching socket format)
    _normalize(n) {
        if (!n) return n;
        // Already camelCase (from socket or localStorage)
        if (n.userId !== undefined) return n;
        return {
            id: n.id,
            userId: n.user_id,
            type: n.notification_type,
            title: n.title,
            message: n.message,
            icon: n.icon,
            color: n.color,
            data: n.metadata || n.data || {},
            timestamp: n.timestamp || n.created_at,
            read: n.is_read ?? false
        };
    }

    async getNotifications() {
        // Try database first
        try {
            const response = await api.get('/api/notifications');
            console.log('✅ Notifications loaded from database');
            const notifications = (response.data.data || []).map(n => this._normalize(n));
            // Cache to localStorage for offline access
            this.saveToLocalStorage(notifications);
            return notifications;
        } catch (error) {
            console.log('⚠️ Database not available, using localStorage fallback for notifications');
            // Fall back to localStorage
            const notifications = this.getFromLocalStorage();
            console.log('✅ Notifications loaded from localStorage fallback');
            return notifications;
        }
    }

    /**
     * Get notifications for specific user
     */
    async getNotificationsForUser(userId) {
        try {
            const response = await api.get(`/api/notifications/user/${userId}`);
            return (response.data.data || []).map(n => this._normalize(n));
        } catch (error) {
            console.error('Error fetching user notifications:', error);
            throw error;
        }
    }

    /**
     * Get unread count
     */
    async getUnreadCount() {
        try {
            const response = await api.get('/api/notifications/unread-count');
            return response.data.data?.count || 0;
        } catch (error) {
            console.error('Error fetching unread count:', error);
            return 0;
        }
    }

    /**
     * Create notification
     */
    async createNotification(notificationData) {
        // Try database first
        try {
            const response = await api.post('/api/notifications', notificationData);
            console.log('✅ Notification created in database');
            return response.data.data;
        } catch (error) {
            console.log('⚠️ Database not available, creating notification locally');

            // Create notification with ID and timestamp for localStorage
            const localNotification = {
                id: Date.now().toString(),
                ...notificationData,
                created_at: new Date().toISOString(),
                read: false,
                source: 'localStorage'
            };

            // Save to localStorage
            const existingNotifications = this.getFromLocalStorage();
            existingNotifications.unshift(localNotification);
            this.saveToLocalStorage(existingNotifications);

            console.log('✅ Notification created in localStorage fallback');
            return localNotification;
        }
    }

    /**
     * Mark notification as read
     */
    async markAsRead(notificationId) {
        // Try database first
        try {
            const response = await api.put(`/api/notifications/${notificationId}/read`);
            console.log('✅ Notification marked as read in database');
            return response.data;
        } catch (error) {
            console.log('⚠️ Database not available, marking notification as read locally');

            // Update in localStorage
            const notifications = this.getFromLocalStorage();
            const updatedNotifications = notifications.map(notification =>
                notification.id === notificationId
                    ? { ...notification, read: true }
                    : notification
            );
            this.saveToLocalStorage(updatedNotifications);

            console.log('✅ Notification marked as read in localStorage');
            return { success: true, source: 'localStorage' };
        }
    }

    /**
     * Mark all notifications as read
     */
    async markAllAsRead() {
        try {
            const response = await api.put('/api/notifications/mark-all-read');
            return response.data;
        } catch (error) {
            console.error('Error marking all as read:', error);
            throw error;
        }
    }

    /**
     * Delete notification
     */
    async deleteNotification(notificationId) {
        try {
            const response = await api.delete(`/api/notifications/${notificationId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting notification:', error);
            throw error;
        }
    }

    /**
     * Clear all notifications
     */
    async clearAll() {
        try {
            const response = await api.delete('/api/notifications');
            return response.data;
        } catch (error) {
            console.error('Error clearing all notifications:', error);
            throw error;
        }
    }

    /**
     * Send onboarding notification
     */
    async sendOnboardingNotification(employeeId, employeeName, designation, legalEntity, managerId) {
        try {
            const response = await api.post('/api/notifications/onboarding', {
                employeeId,
                employeeName,
                designation,
                legalEntity,
                managerId
            });
            return response.data;
        } catch (error) {
            console.error('Error sending onboarding notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * LEAVE MANAGEMENT NOTIFICATIONS
     * ========================================
     */

    /**
     * Send leave request submitted notification (to manager/admin)
     */
    async sendLeaveRequestNotification(leaveRequest) {
        try {
            const response = await api.post('/api/notifications/leave-request', leaveRequest);
            return response.data;
        } catch (error) {
            console.error('Error sending leave request notification:', error);
            throw error;
        }
    }

    /**
     * Send leave approval notification (to employee)
     */
    async sendLeaveApprovalNotification(employeeId, leaveDetails) {
        try {
            const response = await api.post('/api/notifications/leave-approval', {
                employeeId,
                ...leaveDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending leave approval notification:', error);
            throw error;
        }
    }

    /**
     * Send leave rejection notification (to employee)
     */
    async sendLeaveRejectionNotification(employeeId, leaveDetails) {
        try {
            const response = await api.post('/api/notifications/leave-rejection', {
                employeeId,
                ...leaveDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending leave rejection notification:', error);
            throw error;
        }
    }

    /**
     * Send leave balance low warning
     */
    async sendLeaveBalanceLowNotification(employeeId, leaveType, balance) {
        try {
            const response = await api.post('/api/notifications/leave-balance-low', {
                employeeId,
                leaveType,
                balance
            });
            return response.data;
        } catch (error) {
            console.error('Error sending leave balance low notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * ATTENDANCE NOTIFICATIONS
     * ========================================
     */

    /**
     * Send attendance regularization request notification
     */
    async sendAttendanceRegularizationNotification(regularizationRequest) {
        try {
            const response = await api.post('/api/notifications/attendance-regularization', regularizationRequest);
            return response.data;
        } catch (error) {
            console.error('Error sending attendance regularization notification:', error);
            throw error;
        }
    }

    /**
     * Send WFH request notification
     */
    async sendWFHRequestNotification(wfhRequest) {
        try {
            const response = await api.post('/api/notifications/wfh-request', wfhRequest);
            return response.data;
        } catch (error) {
            console.error('Error sending WFH request notification:', error);
            throw error;
        }
    }

    /**
     * Send missing punch notification
     */
    async sendMissingPunchNotification(employeeId, date, type) {
        try {
            const response = await api.post('/api/notifications/missing-punch', {
                employeeId,
                date,
                type
            });
            return response.data;
        } catch (error) {
            console.error('Error sending missing punch notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * TASK MANAGEMENT NOTIFICATIONS
     * ========================================
     */

    /**
     * Send task assigned notification
     */
    async sendTaskAssignedNotification(taskData) {
        try {
            const response = await api.post('/api/notifications/task-assigned', taskData);
            return response.data;
        } catch (error) {
            console.error('Error sending task assigned notification:', error);
            throw error;
        }
    }

    /**
     * Send task deadline approaching notification
     */
    async sendTaskDeadlineNotification(taskData) {
        try {
            const response = await api.post('/api/notifications/task-deadline', taskData);
            return response.data;
        } catch (error) {
            console.error('Error sending task deadline notification:', error);
            throw error;
        }
    }

    /**
     * Send task overdue notification
     */
    async sendTaskOverdueNotification(taskData) {
        try {
            const response = await api.post('/api/notifications/task-overdue', taskData);
            return response.data;
        } catch (error) {
            console.error('Error sending task overdue notification:', error);
            throw error;
        }
    }

    /**
     * Send task status changed notification
     */
    async sendTaskStatusChangedNotification(taskData) {
        try {
            const response = await api.post('/api/notifications/task-status-changed', taskData);
            return response.data;
        } catch (error) {
            console.error('Error sending task status changed notification:', error);
            throw error;
        }
    }

    /**
     * Send task completed notification
     */
    async sendTaskCompletedNotification(taskData) {
        try {
            const response = await api.post('/api/notifications/task-completed', taskData);
            return response.data;
        } catch (error) {
            console.error('Error sending task completed notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * PAYROLL NOTIFICATIONS
     * ========================================
     */

    /**
     * Send payslip ready notification
     */
    async sendPayslipReadyNotification(employeeId, month, year) {
        try {
            const response = await api.post('/api/notifications/payslip-ready', {
                employeeId,
                month,
                year
            });
            return response.data;
        } catch (error) {
            console.error('Error sending payslip ready notification:', error);
            throw error;
        }
    }

    /**
     * Send salary revision notification
     */
    async sendSalaryRevisionNotification(employeeId, revisionDetails) {
        try {
            const response = await api.post('/api/notifications/salary-revision', {
                employeeId,
                ...revisionDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending salary revision notification:', error);
            throw error;
        }
    }

    /**
     * Send flexi component approval notification
     */
    async sendFlexiApprovalNotification(employeeId, flexiDetails, approved) {
        try {
            const response = await api.post('/api/notifications/flexi-approval', {
                employeeId,
                ...flexiDetails,
                approved
            });
            return response.data;
        } catch (error) {
            console.error('Error sending flexi approval notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * DOCUMENT NOTIFICATIONS
     * ========================================
     */

    /**
     * Send document expiry notification
     */
    async sendDocumentExpiryNotification(employeeId, documentDetails) {
        try {
            const response = await api.post('/api/notifications/document-expiry', {
                employeeId,
                ...documentDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending document expiry notification:', error);
            throw error;
        }
    }

    /**
     * Send document required notification
     */
    async sendDocumentRequiredNotification(employeeId, documentType) {
        try {
            const response = await api.post('/api/notifications/document-required', {
                employeeId,
                documentType
            });
            return response.data;
        } catch (error) {
            console.error('Error sending document required notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * VIBE & KUDOS NOTIFICATIONS
     * ========================================
     */

    /**
     * Send vibe mention notification
     */
    async sendVibeMentionNotification(mentionedUserId, mentionedBy, postId, postContent) {
        try {
            const response = await api.post('/api/notifications/vibe-mention', {
                mentionedUserId,
                mentionedBy,
                postId,
                postContent
            });
            return response.data;
        } catch (error) {
            console.error('Error sending vibe mention notification:', error);
            throw error;
        }
    }

    /**
     * Send kudos received notification
     */
    async sendKudosNotification(recipientId, givenBy, kudosDetails) {
        try {
            const response = await api.post('/api/notifications/kudos-received', {
                recipientId,
                givenBy,
                ...kudosDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending kudos notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * APPROVAL NOTIFICATIONS
     * ========================================
     */

    /**
     * Send profile update approval notification
     */
    async sendProfileApprovalNotification(employeeId, approved, changes) {
        try {
            const response = await api.post('/api/notifications/profile-approval', {
                employeeId,
                approved,
                changes
            });
            return response.data;
        } catch (error) {
            console.error('Error sending profile approval notification:', error);
            throw error;
        }
    }

    /**
     * Send approval escalation notification (to admin)
     */
    async sendApprovalEscalationNotification(adminId, approvalDetails) {
        try {
            const response = await api.post('/api/notifications/approval-escalation', {
                adminId,
                ...approvalDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending approval escalation notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * TEAM NOTIFICATIONS (Manager)
     * ========================================
     */

    /**
     * Send team member joined notification
     */
    async sendTeamMemberJoinedNotification(managerId, employeeDetails) {
        try {
            const response = await api.post('/api/notifications/team-member-joined', {
                managerId,
                ...employeeDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending team member joined notification:', error);
            throw error;
        }
    }

    /**
     * Send team absenteeism alert
     */
    async sendTeamAbsenteeismNotification(managerId, absenteeismData) {
        try {
            const response = await api.post('/api/notifications/team-absenteeism', {
                managerId,
                ...absenteeismData
            });
            return response.data;
        } catch (error) {
            console.error('Error sending team absenteeism notification:', error);
            throw error;
        }
    }

    /**
     * ========================================
     * ADMIN NOTIFICATIONS
     * ========================================
     */

    /**
     * Send employee added notification
     */
    async sendEmployeeAddedNotification(adminId, employeeDetails) {
        try {
            const response = await api.post('/api/notifications/employee-added', {
                adminId,
                ...employeeDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending employee added notification:', error);
            throw error;
        }
    }

    /**
     * Send bulk import complete notification
     */
    async sendBulkImportCompleteNotification(adminId, importStats) {
        try {
            const response = await api.post('/api/notifications/bulk-import-complete', {
                adminId,
                ...importStats
            });
            return response.data;
        } catch (error) {
            console.error('Error sending bulk import complete notification:', error);
            throw error;
        }
    }

    /**
     * Send payroll cycle started notification
     */
    async sendPayrollCycleNotification(adminId, cycleDetails) {
        try {
            const response = await api.post('/api/notifications/payroll-cycle', {
                adminId,
                ...cycleDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending payroll cycle notification:', error);
            throw error;
        }
    }

    /**
     * Send system alert notification
     */
    async sendSystemAlertNotification(adminId, alertDetails) {
        try {
            const response = await api.post('/api/notifications/system-alert', {
                adminId,
                ...alertDetails
            });
            return response.data;
        } catch (error) {
            console.error('Error sending system alert notification:', error);
            throw error;
        }
    }

    /**
     * Save notifications to localStorage (for offline access)
     */
    saveToLocalStorage(notifications) {
        try {
            localStorage.setItem('hrportal_notifications', JSON.stringify(notifications));
        } catch (error) {
            console.error('Error saving notifications to localStorage:', error);
        }
    }

    /**
     * Get notifications from localStorage (fallback)
     */
    getFromLocalStorage() {
        // try {
        //     const stored = localStorage.getItem('hrportal_notifications');
        //     return stored ? JSON.parse(stored) : [];
        // } catch (error) {
        //     console.error('Error loading notifications from localStorage:', error);
        //     return [];
        // }
    }
}

export default new NotificationService();
